import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type BoardColumn,
  type WorkflowRule,
  type WorkflowTemplate,
} from "../../lib/api";
import { useUser } from "../../lib/auth";
import {
  Card,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  Tabs,
  Toggle,
  useToast,
} from "../../components/ui";

export function WorkflowScreen() {
  const user = useUser();
  const [tab, setTab] = useState("board");

  const tabs = [
    { id: "board", label: "Live board" },
    ...(user.canPublish
      ? [
          { id: "templates", label: "Templates" },
          { id: "rules", label: "Rules & SLAs" },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Workflow"
        title="Workflows"
        subtitle="The approval route every document follows, and the rules that drive it"
      />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "board" && <Board />}
      {tab === "templates" && <Templates />}
      {tab === "rules" && <Rules />}
    </>
  );
}

const COLUMN_TINT: Record<string, string> = {
  WORK_IN_PROGRESS: "#EFECEE",
  AWAITING_REVIEW: "#FCF6D2",
  AWAITING_APPROVAL: "#F3EEFA",
  QUEUED_FOR_PUBLISH: "#E4F5FB",
  PUBLISHED: "#E6F7DC",
};

function Board() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["board"],
    queryFn: () => api.get<BoardColumn[]>("/workflow/board"),
  });


  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;


  return (
    <div className="grid gap-3 overflow-x-auto lg:grid-cols-5">
      {data?.map((column) => (
        <section
          key={column.status}
          className="min-w-[240px] rounded-card border border-line bg-surface-subtle"
        >
          <header
            className="flex items-center justify-between rounded-t-card px-3 py-2"
            style={{ background: COLUMN_TINT[column.status] ?? "#EFECEE" }}
          >
            <span className="eyebrow !text-ink">{column.label}</span>
            <span className="text-2xs font-extrabold text-ink">{column.count}</span>
          </header>

          <div className="space-y-2 p-2">
            {column.items.length === 0 && (
              <p className="py-6 text-center text-2xs text-muted-soft">Empty</p>
            )}
            {column.items.map((document) => (
                <div
                  key={document.id}
                  className="rounded-card border border-line bg-surface p-2.5"
                >
                  <Link
                    to={`/documents/${document.id}`}
                    className="block text-sm font-bold text-ink no-underline hover:underline"
                  >
                    {document.title}
                  </Link>
                  <div className="mt-0.5 text-2xs text-muted-soft">
                    {document.docNo} · {document.versionLabel} ·{" "}
                    {document.categoryLabel}
                  </div>
                  {/* No buttons here any more. Which action is legal now
                      depends on who you are and, in review, on how many other
                      people have answered — so the board links to the document
                      and lets the detail screen render the actions the server
                      says are available, which is what it already did. */}
                </div>
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Templates() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<WorkflowTemplate[]>("/workflow/templates"),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const selected =
    data?.find((template) => template.id === selectedId) ?? data?.[0] ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <Card padded={false}>
        <ul>
          {data?.map((template) => (
            <li key={template.id}>
              <button
                onClick={() => setSelectedId(template.id)}
                className={`flex w-full items-center gap-2.5 border-b border-line-soft px-3.5 py-3 text-left last:border-0 transition-colors hover:bg-surface-hover ${
                  selected?.id === template.id ? "bg-surface-hover" : ""
                }`}
              >
                <span
                  className="h-8 w-1 shrink-0 rounded-full"
                  style={{
                    background: template.isActive ? "#8DE971" : "#DDD7DA",
                  }}
                />
                <span className="min-w-0">
                  <span className="block truncate text-base font-bold text-ink">
                    {template.name}
                  </span>
                  <span className="block text-2xs text-muted-soft">
                    {template.categoryLabel} · {template.stages.length} stages ·
                    SLA {template.slaDays}d
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {selected && (
        <Card title={selected.name}>
          <p className="mb-4 text-sm text-muted">
            {selected.categoryLabel} · end-to-end SLA {selected.slaDays} working
            days · {selected.runsThisYear} run(s) this year
          </p>
          <ol className="space-y-2">
            {selected.stages.map((stage, index) => (
              <li
                key={`${stage.name}-${index}`}
                className="flex items-center gap-3 rounded-card border border-line bg-surface-subtle px-3 py-2.5"
              >
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink text-2xs font-extrabold text-pearl">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-bold text-ink">
                    {stage.name}
                  </span>
                  <span className="text-2xs text-muted-soft">
                    {stage.role === "NCM"
                      ? "Network Control Manager"
                      : stage.role === "NCE"
                        ? "Network Control Engineer"
                        : stage.role}
                  </span>
                </span>
                <span className="text-2xs font-bold text-muted">
                  SLA {stage.slaDays}d
                </span>
              </li>
            ))}
          </ol>
          <p className="mt-4 border-t border-line-soft pt-3 text-2xs leading-relaxed text-muted-soft">
            Stage editing is available through the API. A template must keep at
            least one Network Control Manager stage — without one, nothing
            approves the document before it publishes.
          </p>
        </Card>
      )}
    </div>
  );
}

function Rules() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["rules"],
    queryFn: () => api.get<WorkflowRule[]>("/workflow/rules"),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.patch<WorkflowRule>(`/workflow/rules/${key}`, { isEnabled: enabled }),
    onSuccess: (rule) => {
      toast(`"${rule.label}" is now ${rule.isEnabled ? "on" : "off"}`);
      void queryClient.invalidateQueries({ queryKey: ["rules"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Could not change", "danger"),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data || data.length === 0) {
    return (
      <Card>
        <Empty title="No routing rules configured" />
      </Card>
    );
  }

  return (
    <Card padded={false}>
      <ul>
        {data.map((rule) => (
          <li
            key={rule.key}
            className="flex items-start gap-3 border-b border-line-soft px-4 py-3 last:border-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-base font-bold text-ink">
                {rule.label}
              </span>
              <span className="block text-sm text-muted">{rule.detail}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-2xs font-bold text-muted-soft">
                {rule.isEnabled ? "On" : "Off"}
              </span>
              <Toggle
                checked={rule.isEnabled}
                label={rule.label}
                disabled={toggle.isPending}
                onChange={(next) =>
                  toggle.mutate({ key: rule.key, enabled: next })
                }
              />
            </span>
          </li>
        ))}
      </ul>
      <p className="border-t border-line-soft px-4 py-2.5 text-2xs text-muted-soft">
        These toggles change behaviour, not just display. Requiring a comment on
        rejection cannot be switched off — an author cannot act on a rejection
        with no reason attached.
      </p>
    </Card>
  );
}
